import { Model, Factory } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { ITEM_TYPE_CODES } from '@/composables/api/NavigationSearch.js'
import {
  rand,
  randBoolean,
  randNumber,
  randProductDescription,
  randAlphaNumeric,
  randProductName,
  randChanceBoolean,
  randUuid,
} from '@ngneat/falso'

import type { MirageServer, MockServer } from './server.js'
import { filterable, getQueryParamValue, pageable } from './utils.js'
import type { OrganizationItemAttributes } from '@/composables/organizationItems.js'

export const model = Model as ModelDefinition<OrganizationItemAttributes>

export const factory = Factory.extend<OrganizationItemAttributes>({
  name() {
    return randProductName()
  },
  print_name() {
    return rand([this.name as string, undefined])
  },
  code() {
    return randAlphaNumeric({ length: 8 }).join('').toUpperCase()
  },
  barcode() {
    return rand([randAlphaNumeric({ length: 12 }).join(''), undefined])
  },
  type_code() {
    const possibleValues = Object.values(ITEM_TYPE_CODES)
    return rand(possibleValues)
  },
  archived() {
    return randBoolean()
  },
  price() {
    return randNumber({ min: 1, max: 500, fraction: 2 })
  },
  price_with_vat() {
    return (this.price as number) * 1.25 // Assuming 25% VAT
  },
  hide_on_consultation() {
    return randBoolean()
  },
  hide_on_consultation_search() {
    return randBoolean()
  },
  hide_on_countersale_search() {
    return randBoolean()
  },
  package_description() {
    if (
      this.type_code === ITEM_TYPE_CODES.PROCEDURE ||
      this.type_code === ITEM_TYPE_CODES.LABORATORY_ANALYSIS ||
      this.type_code === ITEM_TYPE_CODES.LABORATORY_ANALYSIS_PANEL
    ) {
      return undefined
    }
    return rand([randProductDescription(), undefined])
  },
  unit() {
    if (
      this.type_code === ITEM_TYPE_CODES.PROCEDURE ||
      this.type_code === ITEM_TYPE_CODES.LABORATORY_ANALYSIS ||
      this.type_code === ITEM_TYPE_CODES.LABORATORY_ANALYSIS_PANEL
    ) {
      return undefined
    }
    return rand(['mg', 'ml', 'tablets', 'pieces', 'bottles'])
  },
  manufacturer() {
    if (
      this.type_code === ITEM_TYPE_CODES.PROCEDURE ||
      this.type_code === ITEM_TYPE_CODES.LABORATORY_ANALYSIS ||
      this.type_code === ITEM_TYPE_CODES.LABORATORY_ANALYSIS_PANEL
    ) {
      return undefined
    }
    return rand(['VetCorp', 'AnimalPharma', 'PetMeds Inc', undefined])
  },
  active_substance() {
    if (this.type_code !== ITEM_TYPE_CODES.MEDICINE) {
      return undefined
    }
    return rand(['Amoxicillin', 'Carprofen', 'Meloxicam', undefined])
  },
  controlled_substance() {
    if (this.type_code !== ITEM_TYPE_CODES.MEDICINE) {
      return undefined
    }
    return randBoolean()
  },
  vaccination() {
    if (this.type_code !== ITEM_TYPE_CODES.MEDICINE) {
      return undefined
    }
    return randBoolean()
  },
  dosage_units() {
    if (
      this.type_code === ITEM_TYPE_CODES.PROCEDURE ||
      this.type_code === ITEM_TYPE_CODES.LABORATORY_ANALYSIS ||
      this.type_code === ITEM_TYPE_CODES.LABORATORY_ANALYSIS_PANEL
    ) {
      return undefined
    }
    return randNumber({ min: 1, max: 100 }).toString()
  },
  item_list_id() {
    return randNumber().toString()
  },
  vat_group_id() {
    return randNumber({ min: 1, max: 100 }).toString()
  },
  is_main_item() {
    return randBoolean()
  },
  is_sub_item() {
    return !this.is_main_item
  },
  batch_number_required() {
    return randChanceBoolean({ chanceTrue: 0.6 })
  },
  is_controlled_medicine() {
    return randChanceBoolean({ chanceTrue: 0.3 })
  },
  type_id() {
    return randUuid()
  },
  stock_level_amount() {
    return randNumber({ min: -10, max: 100 })
  },
  stock_level_size() {
    return rand(['mg', 'ml', 'pc', 'bottle', 'unit'])
  },
  display_unit_price() {
    return rand([randNumber({ min: 1, max: 50, fraction: 2 }), undefined])
  },
  display_package_price() {
    return rand([randNumber({ min: 50, max: 100, fraction: 2 }), undefined])
  },
  negative_stock_warning() {
    return (this.stock_level_amount as number) < 0
  },
  low_stock() {
    return (this.stock_level_amount as number) < 10
  },
  special_procedure() {
    return rand(['NONE', 'EUTHANASIA', 'NEUTERING', 'MICROCHIPPING', undefined])
  },
})

export function routes(server: MockServer) {
  server.get(
    '/organization/items',
    function (this: MirageServer, schema, request) {
      const organizationItems = schema.all('organizationItem')
      return filterable(organizationItems, request, {
        'department.id': (item, value) => {
          const department = server.schema.find('department', value)
          return item.item_list_id === department?.active_item_list_id
        },
        main_item_id: (item, value) => {
          if (value) {
            return item.main_item_id === value
          }
          return true
        },
        type_id: () => true,
      })
    },
  )

  server.get(
    '/consultations/:parent_lookup_consultation/items/search',
    function (this: MirageServer, schema, request) {
      const searchTerm =
        getQueryParamValue(request.queryParams?.['filter[search]']) || ''
      const items = schema
        .all('organizationItem')
        .filter((item) => typeof item.attrs.type_code === 'string')
        .filter(
          (item) =>
            item.attrs.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.attrs.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.attrs.barcode
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()),
        )
      const { data, meta } = pageable(items, request)
      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    },
  )
}

export function seeds(server: MockServer) {
  server.create('organizationItem', {
    id: '1',
    name: 'Test item 1',
    item_list_id: '1',
    code: '123',
    dosage_units: '10',
    batch_number_required: false,
  })
  server.create('organizationItem', {
    id: '2',
    name: 'Test item 2',
    item_list_id: '1',
    code: '124',
    dosage_units: '10',
    batch_number_required: true,
  })
  server.create('organizationItem', {
    id: '3',
    name: 'Test item 3',
    item_list_id: '1',
    code: '125',
    dosage_units: '1',
    batch_number_required: true,
  })
  server.create('organizationItem', {
    id: '4',
    name: 'Test item 4',
    item_list_id: '2',
    code: '126',
    dosage_units: '10',
    batch_number_required: true,
  })
  server.create('organizationItem', {
    id: '5',
    name: 'Test item 5',
    item_list_id: '2',
    code: '127',
    dosage_units: '1',
    batch_number_required: false,
    main_item_id: '1',
  })
  server.create('organizationItem', {
    id: '6',
    name: 'Consultation test medicine',
    type_code: 'MEDICINE',
  })
  server.create('organizationItem', {
    id: '7',
    name: 'Consultation test supply',
    type_code: 'SUPPLY',
  })
  server.create('organizationItem', {
    id: '8',
    name: 'Consultation test food',
    type_code: 'FOOD',
    stock_level_amount: -5,
  })
  server.create('organizationItem', {
    id: '9',
    name: 'Consultation test procedure',
    type_code: 'PROCEDURE',
    stock_level_amount: 7,
  })
}
